import { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { BUTTON_TYPES } from "constants/SkedButton";
import { useSelector } from "react-redux";
import { getUser } from "selectors/commonSelectors";
import {
  getAccounts,
  getApprovalsAccountsByGroup,
} from "selectors/skedCoreSelectors";
import { SkedButton } from "ui";
import { Alert, AlertType } from "ui/alert";
import { TextInput } from "ui/ecosystems/forms/inputs";
import { Flex } from "ui/commonStyled/layout";

import { useTrackEvent } from "libs/analytics";
import { CollaborationPostStatus } from "features/user/types";
import { COLLABORATION_CREATE_LINKS } from "constants/Events";
import { getPostStatusList } from "utils/user";
import { hasExternalApprovals } from "utils/feature-gates";

import ExternalCollabLinkEdit from "./ExternalCollabLinkEdit";

import ExternalCollabLink from "./ExternalCollabLink";
import {
  ApprovalLink,
  useApprovalExternalData,
  useApprovalLinkMutations,
} from "pages/approvals/approval-queries";

type Props = {
  onEditStateChange: (state: boolean) => void;
};

const ExternalCollabLinks = ({ onEditStateChange }: Props) => {
  const trackEvent = useTrackEvent();
  const { links: stateLinks, themesById: themes } = useApprovalExternalData();
  const { createLink, updateLink } = useApprovalLinkMutations();
  // selectors
  const accounts = useSelector(getAccounts);
  const approvalAccountsData = useSelector(getApprovalsAccountsByGroup);
  const approvalAccounts = approvalAccountsData?.allAccounts ?? [];
  const user = useSelector(getUser);
  // state
  const [search, setSearch] = useState<string>("");
  const [links, setLinks] = useState<ApprovalLink[]>([]);
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);

  // effects
  useEffect(() => {
    if (stateLinks) setLinks(stateLinks);
  }, [stateLinks]);
  useEffect(() => {
    if (onEditStateChange) onEditStateChange(!!editingLinkId);
  }, [editingLinkId]);

  const handleCancelLinkEditing = (link: ApprovalLink) => {
    const isNewLink = link._id === "NEW_LINK";
    if (isNewLink) {
      setLinks(links.filter(({ _id }) => _id !== "NEW_LINK"));
    }
    setEditingLinkId(null);
  };

  const handleSave = (link: ApprovalLink) => {
    const isNewLink = link._id === "NEW_LINK";
    if (isNewLink) {
      trackEvent({
        eventName: COLLABORATION_CREATE_LINKS,
        useServices: ["AMP", "HS"],
      });
      createLink.mutate(link);
      setLinks(links.filter(({ _id }) => _id !== "NEW_LINK"));
    } else {
      updateLink.mutate(link);
    }
    setEditingLinkId(null);
  };

  const defaultStatusKey = useMemo(() => {
    const statuses: CollaborationPostStatus[] = getPostStatusList(user);

    return statuses.find((status) => status.isDefault)?.key || "";
  }, [user]);

  const filteredLinks = useMemo(() => {
    if (!search || search.length <= 3) return links;

    return links.filter((link) =>
      link.linkName?.toLowerCase()?.includes(search.toLowerCase())
    );
  }, [search, links]);

  if (!hasExternalApprovals(user)) return null;

  return (
    <>
      <Flex align="center">
        <Title>Review links</Title>
        <TextInputStyled
          data-testid="approvalLinkSearch"
          placeholder="Search link name"
          value={search}
          onChange={setSearch}
        />
      </Flex>
      <p className="mb-10">
        Below are the review links you have created for sharing with external
        reviewers.
      </p>

      {filteredLinks.map((link) =>
        editingLinkId === link._id ? (
          <ExternalCollabLinkEdit
            key={link._id}
            link={link}
            accounts={approvalAccounts}
            onCancel={handleCancelLinkEditing}
            onSave={handleSave}
            isEditMode={!!editingLinkId}
            defaultStatusKey={defaultStatusKey}
          />
        ) : (
          <ExternalCollabLink
            link={link}
            accounts={accounts}
            statuses={getPostStatusList(user)}
            themes={themes}
            key={link._id}
            onEdit={(link) => setEditingLinkId(link._id)}
            isEditMode={!!editingLinkId}
          />
        )
      )}
      {!defaultStatusKey && (
        <Alert type={AlertType.warning} icon={true}>
          Cannot create a new link. Need to enable collaboration settings.
        </Alert>
      )}
      {editingLinkId ? null : (
        <SkedButton
          data-testid="addExternalCollabLinkButton"
          size="small"
          disabled={!defaultStatusKey}
          onClick={() => {
            const _id = "NEW_LINK";
            setLinks([
              ...links,
              {
                _id,
                accountIds: [],
                deleted: false,
                linkName: "",
                showPostWithState: "",
                acceptState: "",
                rejectState: "",
                themeId: "",
                adminId: "",
                slugId: "",
                isEnabled: true,
                userName: "",
                isStatusChangesVisible: true,
                isExternalNotesVisible: true,
                isPostActivityVisible: true,
              },
            ]);
            setEditingLinkId(_id);
          }}
          type={BUTTON_TYPES.PRIMARY_OUTLINE}
        >
          New Link
        </SkedButton>
      )}
    </>
  );
};

export default ExternalCollabLinks;

const TextInputStyled = styled(TextInput)`
  width: 250px;
  margin-left: 20px;
`;
const Title = styled.h3`
  margin-bottom: 0 !important;
  margin-top: 0 !important;
`;
